import type { CSSProperties } from "react";
import { oklchToRgb, parseHex, rgbToOklch, toHex, toRgba } from "./classCover/colorMix";

/**
 * A category's colour belongs to the teacher — the API stores whatever they picked. Its *weight*
 * on screen belongs to the system: six categories side by side must read as one family, and one
 * of them shouting drowns the other five.
 *
 * So only the hue is adopted. Lightness and colourfulness are imposed, and the numbers below are
 * measured from the six categories of the handoff rather than invented — they sat at L 0.537–0.655
 * and C 0.043–0.101, with every `fg` variant a consistent step below its base. Holding L and C
 * constant is what the handoff was approximating by hand.
 */
const TINT_L = 0.585;
const TINT_C = 0.073;
/** Text on a tinted ground needs to sit below it: measured at L −0.11, C ×0.82 across all six. */
const FG_L_DROP = 0.11;
const FG_C_RATIO = 0.82;
const ON_DARK_L = 0.84;
const ON_DARK_C = 0.085;

/** Neutral, for the "Toutes" entry and for a category the API has not coloured. */
const NEUTRAL = "#6B7280";

/**
 * The API rejects anything that is not #RRGGBB — measured: `blue`, `rgb(…)` and a bare `5A6E93`
 * all come back 400 RESOURCE_CATEGORY_COLOR_INVALID_FORMAT. So this should never fail.
 *
 * It still catches, because `parseHex` throws and this runs inside a render: a colour that somehow
 * predates that validation would take the whole storage screen down rather than draw one grey dot.
 */
const hueOf = (color: string): number | null => {
  try {
    const { C, h } = rgbToOklch(parseHex(color));
    // A grey has no hue to speak of: its angle is numerical noise, so it stays neutral.
    return C < 0.004 ? null : h;
  } catch {
    return null;
  }
};

const atRegister = (hue: number, lightness: number, chroma: number): string =>
  toHex(oklchToRgb({ L: lightness, C: chroma, h: hue }));

/**
 * Brings a chosen colour into the system's register, keeping its hue.
 *
 * A pure yellow and a near-black come back at the same lightness as everything else — which is the
 * point, and also why the pickers preview the result rather than the raw swatch.
 */
export const normaliseTint = (color: string): string => {
  const hue = hueOf(color);
  return hue === null ? NEUTRAL : atRegister(hue, TINT_L, TINT_C);
};

/**
 * Ten hues evenly spaced around the wheel, all at the system's register. One tap, always right —
 * the free picker exists for the teacher who wants a particular hue, not because ten is too few.
 */
export const TINT_PRESETS: readonly string[] = Array.from({ length: 10 }, (_, index) =>
  atRegister((index * 360) / 10, TINT_L, TINT_C),
);

export interface CategoryTint {
  /** The category's colour as the system draws it. */
  base: string;
  /** The same hue, a step darker: for text sitting on a tint of `base`. */
  fg: string;
}

export const tintOf = (color: string | null | undefined): CategoryTint => {
  const hue = color ? hueOf(color) : null;
  if (hue === null) {
    const { L, C, h } = rgbToOklch(parseHex(NEUTRAL));
    return { base: NEUTRAL, fg: toHex(oklchToRgb({ L: L - FG_L_DROP, C, h })) };
  }
  return {
    base: atRegister(hue, TINT_L, TINT_C),
    fg: atRegister(hue, TINT_L - FG_L_DROP, TINT_C * FG_C_RATIO),
  };
};

/*
 * The surfaces below mirror what the six hand-written Tailwind maps used to hold, alpha for alpha.
 * They are inline styles rather than classes for the reason the class covers already are: Tailwind
 * only emits what it can see as a literal, and a colour that arrives from the API is never that.
 */

/** Solid — the dot, a gauge segment. */
export const categoryDotStyle = (color: string | null, dim = false): CSSProperties => ({
  background: dim ? toRgba(parseHex(tintOf(color).base), 0.769) : tintOf(color).base,
});

export const categoryDeepDotStyle = (color: string | null): CSSProperties => {
  const { fg } = tintOf(color);
  const hue = color ? hueOf(color) : null;
  const fill = hue === null ? "#D1D5DB" : atRegister(hue, ON_DARK_L, ON_DARK_C);
  return { background: fill, boxShadow: `inset 0 0 0 2px ${fg}` };
};

export const categoryActiveDotStyle = (color: string | null): CSSProperties => {
  const { base, fg } = tintOf(color);
  return { background: base, boxShadow: `inset 0 0 0 1.5px ${fg}` };
};

/** Selected filter pill: a sustained wash of its tint, a 1.5px rule a step darker, ink text. */
export const categoryPillActiveStyle = (color: string | null): CSSProperties => {
  const { base, fg } = tintOf(color);
  const rgb = parseHex(base);
  return {
    background: toRgba(rgb, 0.26),
    boxShadow: `inset 0 0 0 1.5px ${fg}`,
    color: "rgb(var(--ink))",
  };
};

/** Soft tinted ground for a list pill or a section header. */
export const categorySoftStyle = (color: string | null): CSSProperties => {
  const { base, fg } = tintOf(color);
  return { background: toRgba(parseHex(base), 0.102), color: fg };
};

/**
 * Soft ground and its text for a surface that is dark in the dark theme. `fg` is a step darker than
 * the tint, which is right on white and nearly invisible on the dark ground, so the dark pair is
 * drawn lighter and over a stronger wash. Both pairs travel as custom properties because only the
 * stylesheet knows which theme is on; `categoryAdaptiveClass` picks between them.
 */
export const categoryAdaptiveStyle = (color: string | null): CSSProperties => {
  const { base, fg } = tintOf(color);
  const hue = color ? hueOf(color) : null;
  const rgb = parseHex(base);
  const onDark = hue === null ? "#D1D5DB" : atRegister(hue, ON_DARK_L, ON_DARK_C);
  return {
    "--cat-ground": toRgba(rgb, 0.102),
    "--cat-fg": fg,
    "--cat-ground-dark": toRgba(parseHex(onDark), 0.2),
    "--cat-fg-dark": onDark,
  } as CSSProperties;
};

export const categoryAdaptiveClass = "cat-adaptive";

export const categoryCardStyle = (color: string | null): CSSProperties => {
  const rgb = parseHex(tintOf(color).base);
  return {
    "--cat-border": toRgba(rgb, 0.4),
    "--cat-border-strong": toRgba(rgb, 0.85),
    "--cat-ground": toRgba(rgb, 0.07),
    "--cat-rgb": `${rgb.r} ${rgb.g} ${rgb.b}`,
  } as CSSProperties;
};

/** Card outline, tinted rather than neutral. */
export const categoryBorderStyle = (color: string | null): CSSProperties => ({
  borderColor: toRgba(parseHex(tintOf(color).base), 0.25),
});

/** The preview gradient behind a file extension, and its border. */
export const categoryThumbStyle = (color: string | null): CSSProperties => {
  const rgb = parseHex(tintOf(color).base);
  return {
    background: `linear-gradient(to bottom, ${toRgba(rgb, 0.133)}, ${toRgba(rgb, 0.051)})`,
    borderColor: toRgba(rgb, 0.2),
  };
};

/** The extension itself, over that preview. */
export const categoryExtStyle = (color: string | null, adaptive = false): CSSProperties => {
  const own = toRgba(parseHex(tintOf(color).base), 0.6);
  return { color: adaptive ? `var(--ext-ink, ${own})` : own };
};

/** The category pill laid over a preview: a wash of its tint on white, and a rule. */
export const categoryThumbPillStyle = (color: string | null): CSSProperties => {
  const { base, fg } = tintOf(color);
  const wash = toRgba(parseHex(base), 0.16);
  return {
    background: `linear-gradient(${wash}, ${wash}), #fff`,
    boxShadow: `inset 0 0 0 1px ${toRgba(parseHex(base), 0.5)}`,
    color: fg,
  };
};
